/**
 * Speech Analytics Component
 * Real-time coaching feedback for voice responses
 * Shows pace, filler words, confidence, and clarity analysis
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Button } from '../ui/button';
import { 
    Gauge, Volume2, MessageSquare, Shield, Sparkles,
    TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp,
    Lightbulb, CheckCircle2, AlertCircle, Zap, Clock
} from 'lucide-react';

/**
 * SpeechCoaching - Main component that displays speech analytics
 * 
 * @param {Object} analytics - The analytics data from the backend
 * @param {boolean} isLoading - Whether analytics are being calculated
 * @param {boolean} compact - Whether to show a compact version
 * @param {Function} onClose - Optional callback to close/dismiss the coaching
 */
const SpeechCoaching = ({ analytics, isLoading = false, compact = false, onClose }) => {
    const [expanded, setExpanded] = useState(!compact);

    if (isLoading) {
        return (
            <div className="p-4 rounded-xl bg-indigo-500/10 border border-indigo-500/20 animate-pulse">
                <div className="flex items-center gap-3">
                    <Sparkles className="w-5 h-5 text-indigo-400 animate-spin" />
                    <span className="text-indigo-300 text-sm">Analyzing your speech...</span>
                </div>
            </div>
        );
    }

    if (!analytics) return null;

    const {
        overall_score = 0,
        overall_rating = 'unknown',
        pace = {},
        filler_words = {},
        confidence = {},
        clarity = {},
        top_strengths = [],
        top_improvements = [],
        quick_tips = []
    } = analytics;

    const getRatingColor = (rating) => {
        const colors = {
            excellent: 'text-emerald-400',
            good: 'text-blue-400',
            needs_improvement: 'text-amber-400',
            poor: 'text-rose-400',
            unknown: 'text-gray-400'
        };
        return colors[rating] || colors.unknown;
    };

    const getRatingBg = (rating) => {
        const colors = {
            excellent: 'from-emerald-500/20 to-teal-500/20 border-emerald-500/30',
            good: 'from-blue-500/20 to-cyan-500/20 border-blue-500/30',
            needs_improvement: 'from-amber-500/20 to-orange-500/20 border-amber-500/30',
            poor: 'from-rose-500/20 to-red-500/20 border-rose-500/30',
            unknown: 'from-gray-500/20 to-gray-500/20 border-gray-500/30'
        };
        return colors[rating] || colors.unknown;
    };

    const getScoreColor = (score) => {
        if (score >= 85) return 'text-emerald-400';
        if (score >= 70) return 'text-blue-400';
        if (score >= 50) return 'text-amber-400';
        return 'text-rose-400';
    };

    const getPaceIcon = (rating) => {
        if (rating === 'optimal') return <CheckCircle2 className="w-4 h-4 text-emerald-400" />;
        if (rating === 'slow' || rating === 'too_slow') return <TrendingDown className="w-4 h-4 text-amber-400" />;
        if (rating === 'fast' || rating === 'too_fast') return <TrendingUp className="w-4 h-4 text-amber-400" />;
        return <Minus className="w-4 h-4 text-gray-400" />;
    };

    // Compact view - just shows overall score and key metrics
    if (compact && !expanded) {
        return (
            <div 
                className={`p-4 rounded-xl bg-gradient-to-br ${getRatingBg(overall_rating)} border cursor-pointer transition-all hover:scale-[1.01]`}
                onClick={() => setExpanded(true)}
                data-testid="speech-coaching-compact"
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-indigo-400" />
                            <span className="font-semibold text-white">Speech Coaching</span>
                        </div>
                        <div className={`text-2xl font-bold ${getScoreColor(overall_score)}`}>
                            {Math.round(overall_score)}
                        </div>
                        <Badge variant="outline" className={`${getRatingColor(overall_rating)} border-current`}>
                            {overall_rating.replace('_', ' ')}
                        </Badge>
                    </div>
                    <div className="flex items-center gap-4 text-sm text-white/60">
                        <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {pace.words_per_minute || 0} WPM
                        </span>
                        <span className="flex items-center gap-1">
                            <MessageSquare className="w-4 h-4" />
                            {filler_words.total_count || 0} fillers
                        </span>
                        <ChevronDown className="w-5 h-5" />
                    </div>
                </div>
            </div>
        );
    }

    // Full view
    return (
        <div 
            className={`rounded-2xl bg-gradient-to-br ${getRatingBg(overall_rating)} border overflow-hidden`}
            data-testid="speech-coaching-full"
        >
            {/* Header */}
            <div className="p-4 border-b border-white/10 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Sparkles className="w-6 h-6 text-indigo-400" />
                    <div>
                        <h3 className="font-bold text-white">Real-Time Speech Coaching</h3>
                        <p className="text-xs text-white/50">AI-powered feedback on your delivery</p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className={`text-3xl font-bold ${getScoreColor(overall_score)}`}>
                        {Math.round(overall_score)}
                    </div>
                    <Badge 
                        variant="outline" 
                        className={`${getRatingColor(overall_rating)} border-current text-sm px-3`}
                    >
                        {overall_rating.replace('_', ' ')}
                    </Badge>
                    {compact && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => setExpanded(false)}
                            className="text-white/50 hover:text-white"
                        >
                            <ChevronUp className="w-5 h-5" />
                        </Button>
                    )}
                    {onClose && (
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={onClose}
                            className="text-white/50 hover:text-white"
                        >
                            ×
                        </Button>
                    )}
                </div>
            </div>

            {/* Metrics Grid */}
            <div className="p-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Pace */}
                <div className="p-3 rounded-xl bg-black/20">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Pace</span>
                        {getPaceIcon(pace.rating)}
                    </div>
                    <div className="text-xl font-bold text-white">
                        {pace.words_per_minute || 0}
                        <span className="text-sm font-normal text-white/50 ml-1">WPM</span>
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                        {pace.rating === 'optimal' ? 'Perfect pace' : 
                         pace.rating === 'slow' || pace.rating === 'too_slow' ? 'Speak faster' :
                         pace.rating === 'fast' || pace.rating === 'too_fast' ? 'Slow down' : 
                         'Calculating...'}
                    </p>
                </div>

                {/* Filler Words */}
                <div className="p-3 rounded-xl bg-black/20">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Fillers</span>
                        {filler_words.rating === 'excellent' ? (
                            <CheckCircle2 className="w-4 h-4 text-emerald-400" />
                        ) : filler_words.rating === 'high' ? (
                            <AlertCircle className="w-4 h-4 text-rose-400" />
                        ) : (
                            <Minus className="w-4 h-4 text-amber-400" />
                        )}
                    </div>
                    <div className="text-xl font-bold text-white">
                        {filler_words.total_count || 0}
                        <span className="text-sm font-normal text-white/50 ml-1">words</span>
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                        {filler_words.filler_density?.toFixed(1) || 0}% density
                    </p>
                </div>

                {/* Confidence */}
                <div className="p-3 rounded-xl bg-black/20">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Confidence</span>
                        <Shield className={`w-4 h-4 ${
                            confidence.level === 'high' ? 'text-emerald-400' :
                            confidence.level === 'medium' ? 'text-amber-400' :
                            'text-rose-400'
                        }`} />
                    </div>
                    <div className="text-xl font-bold text-white capitalize">
                        {confidence.level || 'N/A'}
                    </div>
                    <Progress 
                        value={(confidence.score || 0) * 100} 
                        className="h-1 mt-2 bg-white/10"
                    />
                </div>

                {/* Clarity */}
                <div className="p-3 rounded-xl bg-black/20">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-white/50 uppercase tracking-wider">Clarity</span>
                        <Volume2 className={`w-4 h-4 ${
                            clarity.clarity_score >= 0.8 ? 'text-emerald-400' :
                            clarity.clarity_score >= 0.6 ? 'text-amber-400' :
                            'text-rose-400'
                        }`} />
                    </div>
                    <div className="text-xl font-bold text-white">
                        {Math.round((clarity.clarity_score || 0) * 100)}%
                    </div>
                    <p className="text-xs text-white/40 mt-1">
                        ~{clarity.average_sentence_length?.toFixed(0) || 0} words/sentence
                    </p>
                </div>
            </div>

            {/* Detected Filler Words */}
            {filler_words.detected_fillers && Object.keys(filler_words.detected_fillers).length > 0 && (
                <div className="px-4 pb-2">
                    <p className="text-xs text-white/50 mb-2">Detected fillers:</p>
                    <div className="flex flex-wrap gap-2">
                        {Object.entries(filler_words.detected_fillers).slice(0, 5).map(([word, count]) => (
                            <Badge 
                                key={word} 
                                variant="secondary" 
                                className="bg-rose-500/20 text-rose-300 border-rose-500/30"
                            >
                                &quot;{word}&quot; × {count}
                            </Badge>
                        ))}
                    </div>
                </div>
            )}

            {/* Strengths & Improvements */}
            <div className="p-4 grid md:grid-cols-2 gap-4">
                {/* Strengths */}
                {top_strengths.length > 0 && (
                    <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                        <h4 className="text-sm font-semibold text-emerald-400 mb-2 flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4" />
                            What You Did Well
                        </h4>
                        <ul className="space-y-1">
                            {top_strengths.map((strength, i) => (
                                <li key={`strength-${i}`} className="text-sm text-white/70 flex items-start gap-2">
                                    <span className="text-emerald-400 mt-1">•</span>
                                    {strength}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}

                {/* Improvements */}
                {top_improvements.length > 0 && (
                    <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                        <h4 className="text-sm font-semibold text-amber-400 mb-2 flex items-center gap-2">
                            <TrendingUp className="w-4 h-4" />
                            Areas to Improve
                        </h4>
                        <ul className="space-y-1">
                            {top_improvements.map((improvement, i) => (
                                <li key={`improvement-${i}`} className="text-sm text-white/70 flex items-start gap-2">
                                    <span className="text-amber-400 mt-1">•</span>
                                    {improvement}
                                </li>
                            ))}
                        </ul>
                    </div>
                )}
            </div>

            {/* Quick Tips */}
            {quick_tips.length > 0 && (
                <div className="px-4 pb-4">
                    <div className="p-3 rounded-xl bg-indigo-500/10 border border-indigo-500/20">
                        <h4 className="text-sm font-semibold text-indigo-400 mb-2 flex items-center gap-2">
                            <Lightbulb className="w-4 h-4" />
                            Quick Tips
                        </h4>
                        <ul className="space-y-1">
                            {quick_tips.map((tip, i) => (
                                <li key={`tip-${i}`} className="text-sm text-white/70">
                                    {tip}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            )}
        </div>
    );
};

/**
 * InlineSpeechScore - A minimal inline badge showing just the score
 */
export const InlineSpeechScore = ({ score, rating }) => {
    if (score === undefined || score === null) return null;

    const getColor = () => {
        if (score >= 85) return 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30';
        if (score >= 70) return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
        if (score >= 50) return 'bg-amber-500/20 text-amber-400 border-amber-500/30';
        return 'bg-rose-500/20 text-rose-400 border-rose-500/30';
    };

    return (
        <Badge variant="outline" className={`${getColor()} gap-1`}>
            <Zap className="w-3 h-3" />
            {Math.round(score)}
        </Badge>
    );
};

/**
 * SpeechMetricBadge - Shows a single metric as a badge
 */
export const SpeechMetricBadge = ({ label, value, unit, status }) => {
    const getStatusColor = () => {
        switch (status) {
            case 'good':
            case 'excellent':
            case 'optimal':
                return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
            case 'warning':
            case 'needs_work':
            case 'slow':
            case 'fast':
                return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
            case 'error':
            case 'high':
            case 'too_slow':
            case 'too_fast':
            case 'low':
                return 'bg-rose-500/10 text-rose-400 border-rose-500/20';
            default:
                return 'bg-white/5 text-white/60 border-white/10';
        }
    };

    return (
        <div className={`px-3 py-2 rounded-lg border ${getStatusColor()}`}>
            <p className="text-xs opacity-70">{label}</p>
            <p className="font-semibold">
                {value}
                {unit && <span className="text-xs ml-1 opacity-70">{unit}</span>}
            </p>
        </div>
    );
};

export default SpeechCoaching;
